import { useEffect, useState } from "react";
import { useOutletContext } from "react-router-dom";
import axios from "axios";
import { Card, ListGroup, Badge, Spinner, Alert, Accordion } from "react-bootstrap";

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;

const MeasurementDetails = () => {
    const { measurementId, auth, onLogout } = useOutletContext();
    const [measurement, setMeasurement] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    useEffect(() => {
        const fetchMeasurement = async () => {
            try {
                setLoading(true);
                const response = await axios.get(`${API_BASE_URL}/measurements/${measurementId}`, {
                    headers: { Authorization: `Bearer ${auth.token}` } // Add Authorization header
                });
                setMeasurement(response.data);
            } catch (err) {
                if (err.response?.status === 401) {
                    onLogout();
                } else if (err.response?.status === 404) {
                    setError("Measurement not found");
                } else if (err.response?.status >= 500) {
                    setError("Server error occurred. Please try again later.");
                } else {
                    setError(err.response?.data?.message || "Failed to load measurement details");
                }
            } finally {
                setLoading(false);
            }
        };

        if (measurementId && auth?.token) {
            fetchMeasurement();
        } else {
            setError("Invalid measurement ID or authentication token");
            setLoading(false);
        }
    }, [measurementId, auth.token, onLogout]);

    if (loading) return <Spinner animation="border" />;
    if (error) return <Alert variant="danger">{error}</Alert>;
    if (!measurement) return <Alert variant="warning">Measurement not found</Alert>;

    return (
        <Card>
            <Card.Header className="d-flex justify-content-between align-items-center">
                <h4 className="mb-0">{measurement.filename}</h4>
                <Badge bg="secondary" className="fs-6">
                    {measurement.measurement_type}
                </Badge>
            </Card.Header>
            <Card.Body>
                <Accordion defaultActiveKey={['0', '1', '2']} alwaysOpen>
                    {/* General Information Section */}
                    <Accordion.Item eventKey="0">
                        <Accordion.Header>General Information</Accordion.Header>
                        <Accordion.Body>
                            <ListGroup variant="flush">
                                <ListGroup.Item>
                                    <strong>File Name:</strong> {measurement.filename}
                                </ListGroup.Item>
                                <ListGroup.Item>
                                    <strong>User:</strong> {measurement.user}
                                </ListGroup.Item>
                                <ListGroup.Item>
                                    <strong>Description:</strong> {measurement.run_description || "None"}
                                </ListGroup.Item>
                                <ListGroup.Item>
                                    <strong>Created:</strong> {new Date(measurement.created_at).toLocaleString()}
                                </ListGroup.Item>
                                <ListGroup.Item>
                                    <strong>Updated:</strong> {measurement.updated_at ? new Date(measurement.updated_at).toLocaleString() : "Never"}
                                </ListGroup.Item>
                            </ListGroup>
                        </Accordion.Body>
                    </Accordion.Item>

                    {/* Network Information Section */}
                    <Accordion.Item eventKey="1">
                        <Accordion.Header>Network Information</Accordion.Header>
                        <Accordion.Body>
                            <ListGroup variant="flush">
                                <ListGroup.Item>
                                    <strong>SSID:</strong> {measurement.ssid || "N/A"}
                                </ListGroup.Item>
                                <ListGroup.Item>
                                    <strong>IP Address:</strong> {measurement.ip_address || "N/A"}
                                </ListGroup.Item>
                            </ListGroup>
                        </Accordion.Body>
                    </Accordion.Item>

                    {/* Sensor Information Section */}
                    <Accordion.Item eventKey="2">
                        <Accordion.Header>Sensor Information</Accordion.Header>
                        <Accordion.Body>
                            <ListGroup variant="flush">
                                <ListGroup.Item>
                                    <strong>Stations:</strong> {measurement.stations?.join(", ") || "None"}
                                </ListGroup.Item>
                                <ListGroup.Item className="pt-3">
                                    <strong>Sensors:</strong>
                                    {measurement.sensors_with_channels?.length > 0 ? (
                                        <ListGroup as="ol" numbered className="mt-2">
                                            {measurement.sensors_with_channels.map((sensor, index) => (
                                                <ListGroup.Item as="li" key={index}>
                                                    Serial: {sensor.serial_number} | Channel: {sensor.channel}
                                                </ListGroup.Item>
                                            ))}
                                        </ListGroup>
                                    ) : ( " None" )}
                                </ListGroup.Item>
                                <ListGroup.Item>
                                    <strong>Number of Sensors:</strong> {measurement.sensors_with_channels?.length || 0}
                                </ListGroup.Item>
                            </ListGroup>
                        </Accordion.Body>
                    </Accordion.Item>
                </Accordion>
            </Card.Body>
        </Card>
    );
};

export default MeasurementDetails;